import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  BookOpen,
  TrendingUp,
  Award,
  Gift,
  Clock,
  Lightbulb,
  CheckCheck,
  Trash2,
} from 'lucide-react';
import {
  useInboxNotifications,
  useUnreadCount,
  useInboxOpen,
  useInboxActions,
  type InboxNotification,
  type NotificationCategory,
} from '@/stores/useNotificationInbox';

// Icon mapping for notification categories
const CATEGORY_ICONS: Record<NotificationCategory, React.ComponentType<{ className?: string }>> = {
  study_reminder: BookOpen,
  progress_update: TrendingUp,
  achievement: Award,
  celebration: Award,
  trial_offer: Gift,
  exam_countdown: Clock,
  tip: Lightbulb,
  system: Bell,
};

const CATEGORY_COLORS: Record<NotificationCategory, string> = {
  study_reminder: 'text-primary-600 bg-primary-50',
  progress_update: 'text-success-600 bg-success-50',
  achievement: 'text-warning-600 bg-warning-50',
  celebration: 'text-celebrate-700 bg-celebrate-100',
  trial_offer: 'text-accent-600 bg-accent-50',
  exam_countdown: 'text-secondary-600 bg-secondary-50',
  tip: 'text-primary-600 bg-primary-50',
  system: 'text-neutral-600 bg-neutral-50',
};

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface NotificationItemProps {
  notification: InboxNotification;
  onMarkRead: (id: string) => void;
  onRemove: (id: string) => void;
  onAction: (url: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = React.memo(({
  notification,
  onMarkRead,
  onRemove,
  onAction,
}) => {
  const Icon = CATEGORY_ICONS[notification.category] || Bell;
  const colorClass = CATEGORY_COLORS[notification.category] || CATEGORY_COLORS.system;

  const handleClick = () => {
    if (!notification.read) {
      onMarkRead(notification.id);
    }
    if (notification.actionUrl) {
      onAction(notification.actionUrl);
    }
  };

  return (
    <div
      className={`
        group relative px-4 py-3 transition-colors cursor-pointer
        ${notification.read
          ? 'bg-white'
          : 'bg-primary-50/40'
        }
        hover:bg-neutral-50
      `}
      onClick={handleClick}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${colorClass.split(' ').slice(2).join(' ')}`}>
          <Icon className={`w-4 h-4 ${colorClass.split(' ').slice(0, 2).join(' ')}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-medium ${
              notification.read
                ? 'text-neutral-700'
                : 'text-neutral-900'
            }`}>
              {notification.title}
            </p>
            <span className="flex-shrink-0 text-xs text-neutral-500">
              {formatRelativeTime(notification.createdAt)}
            </span>
          </div>
          <p className="mt-0.5 text-sm text-neutral-600 line-clamp-2">
            {notification.message}
          </p>
          {notification.actionLabel && notification.actionUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAction(notification.actionUrl!);
                onMarkRead(notification.id);
              }}
              className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {notification.actionLabel}
            </button>
          )}
        </div>

        {/* Unread indicator */}
        {!notification.read && (
          <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary-500" />
        )}

        {/* Remove button (shows on hover) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(notification.id);
          }}
          className="absolute right-2 top-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-neutral-100 transition-all"
          aria-label="Remove notification"
        >
          <X className="w-3.5 h-3.5 text-neutral-400" />
        </button>
      </div>
    </div>
  );
});

NotificationItem.displayName = 'NotificationItem';

/**
 * Notification Bell Button - shows in header
 */
export const NotificationBell: React.FC = () => {
  const unreadCount = useUnreadCount();
  const { toggleOpen } = useInboxActions();

  return (
    <button
      onClick={toggleOpen}
      className="relative p-2 text-neutral-600 hover:text-neutral-900 rounded-lg hover:bg-neutral-50 transition-all duration-200"
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-error-500 rounded-full">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};

/**
 * Notification Tray — a dropdown panel rendered in a PORTAL on document.body so it
 * escapes the sidebar's `overflow-y-auto` clip (which was slicing the panel off
 * the left edge of the screen). It measures its sibling bell and positions itself
 * next to it with fixed coordinates.
 *
 * `placement` says where the bell lives, which sets the open direction:
 * - 'top': desktop sidebar footer (bell bottom-left) → opens UP and to the RIGHT.
 * - 'bottom': mobile top bar (bell top-right) → opens DOWN, right-aligned.
 *
 * Two instances share one open-state (desktop + mobile). Only the instance whose
 * bell is actually visible (offsetParent !== null) renders a panel.
 */
const PANEL_WIDTH = 384;
const PANEL_GAP = 10;
const SCREEN_MARGIN = 16;

export const NotificationTray: React.FC<{ placement?: 'top' | 'bottom' }> = ({
  placement = 'bottom',
}) => {
  const notifications = useInboxNotifications();
  const isOpen = useInboxOpen();
  const { setIsOpen, markAsRead, markAllAsRead, removeNotification, clearAll } = useInboxActions();
  const navigate = useNavigate();
  const trayRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Measure the bell (this tray's parent wrapper) whenever the panel is open, and
  // keep it aligned on resize/scroll. Skips when this instance's bell is hidden
  // (display:none → offsetParent null) so only the visible breakpoint shows a panel.
  useLayoutEffect(() => {
    if (!isOpen) {
      setRect(null);
      return;
    }
    const measure = () => {
      const wrap = anchorRef.current?.parentElement;
      setRect(wrap && wrap.offsetParent !== null ? wrap.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (trayRef.current && !trayRef.current.contains(event.target as Node)) {
        // Ignore clicks on the bell button (which has its own toggle)
        const target = event.target as HTMLElement;
        if (!target.closest('[aria-label*="Notifications"]')) {
          setIsOpen(false);
        }
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, setIsOpen]);

  const handleAction = (url: string) => {
    setIsOpen(false);
    if (url.startsWith('http')) window.open(url, '_blank');
    else navigate(url);
  };

  const unreadNotifications = notifications.filter((n) => !n.read);
  const hasUnread = unreadNotifications.length > 0;

  // Fixed coordinates anchored to the bell, clamped so BOTH edges stay on-screen
  // regardless of where the bell sits, plus a viewport-height cap (the list scrolls
  // internally) so a high anchor can't push the panel off the top.
  const panelStyle: React.CSSProperties = { position: 'fixed', zIndex: 60 };
  if (rect) {
    const vw = window.innerWidth;
    const width = Math.min(PANEL_WIDTH, vw - 2 * SCREEN_MARGIN);
    panelStyle.maxHeight = `calc(100vh - ${2 * SCREEN_MARGIN}px)`;
    if (placement === 'top') {
      panelStyle.bottom = window.innerHeight - rect.top + PANEL_GAP;
      panelStyle.left = Math.max(SCREEN_MARGIN, Math.min(rect.left, vw - width - SCREEN_MARGIN));
    } else {
      panelStyle.top = rect.bottom + PANEL_GAP;
      // Bound the right offset so the LEFT edge can't run off-screen either.
      panelStyle.right = Math.min(
        Math.max(SCREEN_MARGIN, vw - rect.right),
        vw - width - SCREEN_MARGIN,
      );
    }
  }

  const panel = (
    <div
      ref={trayRef}
      style={panelStyle}
      className="w-[min(384px,calc(100vw-2rem))] bg-white rounded-2xl shadow-ledge-lg border-2 border-neutral-200 overflow-hidden animate-fade-in"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b-2 border-neutral-100 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-neutral-900">Notifications</h3>
        <div className="flex items-center gap-1">
          {hasUnread && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-700 px-2 py-1 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
              aria-label="Clear all notifications"
            >
              <Trash2 className="w-4 h-4 text-neutral-400" />
            </button>
          )}
        </div>
      </div>

      {/* Notification List */}
      <div className="max-h-[min(60vh,420px)] overflow-y-auto divide-y divide-neutral-100">
        {notifications.length === 0 ? (
          <div className="py-12 px-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-neutral-50 flex items-center justify-center">
              <Bell className="w-6 h-6 text-neutral-300" />
            </div>
            <p className="text-sm font-semibold text-neutral-700">You&apos;re all caught up</p>
            <p className="text-xs text-neutral-400 mt-1">Study reminders and updates from Pam will show up here.</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={markAsRead}
              onRemove={removeNotification}
              onAction={handleAction}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2.5 border-t-2 border-neutral-100 bg-neutral-50">
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/settings');
            }}
            className="w-full text-center text-xs font-semibold text-neutral-500 hover:text-neutral-800 transition-colors"
          >
            Notification settings
          </button>
        </div>
      )}
    </div>
  );

  // The anchor span stays in the DOM (display:contents, no layout impact) so we can
  // measure the bell wrapper; the panel itself is portalled to body.
  return (
    <span ref={anchorRef} className="contents">
      {isOpen && rect && createPortal(panel, document.body)}
    </span>
  );
};

export default NotificationTray;
